import RoundTab from "components/RoundTabButtons/RoundTab";
import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import CreateProjectDropdown from "../../../components/DropdownStyle/CreateProjectDropdown";
import ComponentCircleLoader from "../../../components/Loaders/ComponentCircleLoader";
import Map from "../../../components/ReactGoogleMapsApi/GoogleMap";
import Select from "../../../components/ReactSelectDropdown";
import { useScrollShadow } from "../../../customHooks/useScrollShadow";
import { TabSearchGrid } from "../../../styledComponents/ProjectsNew";
import {
  TabButton,
  TabButtonContainer,
} from "../../../styledComponents/buttons";
import {
  CommonGrid,
  ContainerNew,
  FilterLabel,
  HeaderContainer,
  InputWithLabelSection,
  PageTitle,
} from "../../../styledComponents/common";
import { StickyFilterContainer } from "../../../styledComponents/timesheet";
import {
  MapAndAlertContainer,
  MapTopAlert,
} from "../RouteMapNew/routeMapStyles";

const LiveLocation = ({
  liveLocationList, // API: latest locations
  getLatestLocationList, // action
  selectedOrganization, // current org
  liveLocationListLoading, // loading flag
  getOfficesShortList, // action
  officesShortList, // office dropdown
  onlineMembers, // API: clocked-in users
  getAllMemberLastClockInOut, // action
}) => {
  const { t } = useTranslation();
  const showShadow = useScrollShadow(60);
  const orgType = selectedOrganization?.type || "operation";
  const isOperationType = orgType === "operation";

  // UI
  const [selectedTab, setSelectedTab] = useState("live");
  const [awayUsers, setAwayUsers] = useState([]);
  const [liveUsers, setLiveUsers] = useState([]);
  const [userCurrentLocation, setUserCurrentLocation] = useState(null);

  // office filter
  const [selectedOffice, setSelectedOffice] = useState({
    value: null,
    label: "(All Teams)",
  });
  const [officeOptions, setOfficeOptions] = useState([]);

  const officeFilterOptions = [
    { value: null, label: "(All Teams)" },
    { value: "office_users", label: "(Office users)" },
    { value: "remote_users", label: "(Remote users)" },
    ...officeOptions,
  ];

  const onTabSwitch = value => setSelectedTab(value);
  const selectOffice = e => setSelectedOffice(e);

  // detect own location
  useEffect(() => {
    fetch("https://hutils.loxal.net/whois")
      .then(resp => resp.json())
      .then(res => {
        if (res && res.latitude && res.longitude) {
          setUserCurrentLocation({
            lat: res.latitude,
            lng: res.longitude,
          });
        }
      })
      .catch(() => {});
  }, []);

  // fetch members + offices when org changes
  useEffect(() => {
    if (selectedOrganization?.id) {
      getAllMemberLastClockInOut({ organization_id: selectedOrganization.id });
      getOfficesShortList({ organization_id: selectedOrganization.id });
    }
  }, [selectedOrganization]);

  // fetch latest locations after online members loaded
  useEffect(() => {
    if (selectedOrganization?.id && onlineMembers && onlineMembers.length > 0) {
      // ❌ COMMENTED OUT: oldestClockin logic
      // const oldestClockin =
      //   onlineMembers[onlineMembers.length - 1].clockin_timestamp;

      getLatestLocationList({
        organization_id: selectedOrganization.id,
        // ✅ Use start of day instead of oldest clock-in
        from: moment().startOf("day").unix(),
        to: moment().endOf("day").unix(),
        officeFilter: selectedOffice?.value,
      });
    }
  }, [onlineMembers, selectedOffice, selectedOrganization]);

  // prepare office dropdown
  useEffect(() => {
    if (officesShortList?.length > 0) {
      setSelectedOffice({ value: null, label: "(All Teams)" });
      const newArr = officesShortList.map(office => ({
        label: office.name,
        value: office.id,
      }));
      setOfficeOptions(newArr);
    }
  }, [officesShortList]);

  // ✅ merge onlineMembers + locations safely
  useEffect(() => {
    if (!liveLocationList || !onlineMembers) return;

    const live = [];
    const away = [];
    const now = moment();
    const processedIds = new Set(); // Track processed user IDs

    // ✅ Process ONLINE members
    onlineMembers.forEach(user => {
      processedIds.add(user.id);

      const locationUser = liveLocationList.find(
        loc => loc.employee.id === user.id,
      );

      const lastClockinTime = moment.unix(user.clockin_timestamp);

      // fallback lat/lng from clockin if location not found
      const lat = parseFloat(
        locationUser?.latest_location?.lat || user.clockin_lat,
      );
      const lng = parseFloat(
        locationUser?.latest_location?.long || user.clockin_long,
      );

      // ✅ skip invalid coords
      if (!isFinite(lat) || !isFinite(lng)) return;

      // ✅ LOGIC: locationUser exists AND user is online → LIVE, else → AWAY
      if (locationUser) {
        // locationUser exists + user is online → LIVE
        live.push({
          ...locationUser,
          latest_location: {
            ...locationUser.latest_location,
            lat,
            long: lng,
          },
          from: moment().startOf("day").unix(),
          to: moment().endOf("day").unix(),
        });
      } else {
        // No locationUser → AWAY (last detected position, use clock-in fallback)
        away.push({
          ...user,
          location: { lat, lng },
          from: lastClockinTime.unix(),
          to: now.unix(),
        });
      }
    });

    // ✅ Process OFFLINE members (not in onlineMembers but have location data)
    liveLocationList.forEach(locationUser => {
      const employeeId = locationUser?.employee?.id;

      // Skip if already processed as online member
      if (processedIds.has(employeeId)) return;

      // Skip if no valid coords
      const lat = parseFloat(locationUser?.latest_location?.lat);
      const lng = parseFloat(locationUser?.latest_location?.long);
      if (!isFinite(lat) || !isFinite(lng)) return;

      // Offline user with location data → AWAY (last detected position)
      away.push({
        ...locationUser,
        latest_location: {
          ...locationUser.latest_location,
          lat,
          long: lng,
        },
        from: locationUser?.latest_location?.timestamp
          ? moment.unix(locationUser.latest_location.timestamp).unix()
          : now.unix(),
        to: now.unix(),
      });
    });

    setLiveUsers(live);
    setAwayUsers(away);
  }, [liveLocationList, onlineMembers]);

  return (
    <div className="content">
      <ContainerNew>
        <PageTitle>{t("live_location")}</PageTitle>

        {/* Sticky filters */}
        <StickyFilterContainer
          showShadow={showShadow}
          margin="0"
          paddingLeft="0"
          paddingRight="0"
        >
          <HeaderContainer margin="0px">
            <TabSearchGrid columns="auto auto">
              {/* Tab filter */}
              <InputWithLabelSection>
                <FilterLabel>{t("type")}</FilterLabel>
                <CommonGrid columns="auto auto" gap="5px">
                  <RoundTab
                    text={t("live")}
                    selected={selectedTab === "live"}
                    onClick={() => onTabSwitch("live")}
                    showIcon={false}
                  />
                  <RoundTab
                    text={t("last_detected_position")}
                    selected={selectedTab === "away"}
                    onClick={() => onTabSwitch("away")}
                    showIcon={false}
                  />
                </CommonGrid>
                {/* <TabButtonContainer width="max-content" disableResponsive>
          <TabButton
            onClick={() => onTabSwitch("live")}
            selected={selectedTab}
            index="live"
          >
            {t("live")}
          </TabButton>
          <TabButton
            onClick={() => onTabSwitch("away")}
            selected={selectedTab}
            index="away"
          >
            {t("last_detected_position")}
          </TabButton>
        </TabButtonContainer> */}
              </InputWithLabelSection>

              {/* Office filter */}
              {isOperationType && ( // only show office filter for operation type
                <InputWithLabelSection style={{ minWidth: `240px` }}>
                  <FilterLabel>Team</FilterLabel>
                  <Select
                    isSearchable={false}
                    value={selectedOffice}
                    options={officeFilterOptions}
                    placeholder=""
                    onChange={e => selectOffice(e)}
                    styles={CreateProjectDropdown(null)}
                  />
                </InputWithLabelSection>
              )}
            </TabSearchGrid>
          </HeaderContainer>
        </StickyFilterContainer>

        {/* Map and Alerts */}
        <MapAndAlertContainer>
          {liveLocationListLoading ? (
            <MapTopAlert fontSize="15px" height="48px">
              <ComponentCircleLoader padding="10px 0" size={16} color="#fff" />
            </MapTopAlert>
          ) : (
            <>
              {/*Always show counts */}
              <MapTopAlert fontSize="15px" height="48px">
                {t("live")}: {liveUsers.length} | {t("away")}:{" "}
                {awayUsers.length}
              </MapTopAlert>

              {/*Conditional messages */}
              {selectedTab === "away" && awayUsers.length === 0 && (
                <MapTopAlert fontSize="15px" height="48px">
                  {t("no_one_is_offline_now")}
                </MapTopAlert>
              )}
              {selectedTab === "live" && liveUsers.length === 0 && (
                <MapTopAlert fontSize="15px" height="48px">
                  {t("no_one_is_live_now")}
                </MapTopAlert>
              )}
            </>
          )}

          {/* Map */}
          <Map
            userList={selectedTab === "away" ? awayUsers : liveUsers}
            isAwayUsers={selectedTab === "away"}
            userCurrentLocation={userCurrentLocation}
          />
        </MapAndAlertContainer>
      </ContainerNew>
    </div>
  );
};

export default LiveLocation;
